import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import api, { extractUploadedAssetPath } from '../utils/api';
import { prepareFileForUpload } from '../utils/imageUploadCompress';
import ImageCropDialog from './ImageCropDialog';
import { useIdCardAssets } from '../hooks/useIdCardAssets';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import {
  Settings as SettingsIcon,
  School,
  Calendar,
  Save,
  Image,
  MapPin,
  IdCard,
  Menu,
  Loader2,
  Mail,
  MessageCircle,
} from 'lucide-react';
import IdCardSetupPanel from './IdCardSetupPanel';
import SettingsSidebar from './settings/SettingsSidebar';
import MobileInput from './ui/mobile-input';
import DateInput from './ui/date-input';
import { validateMobile } from '../utils/phoneValidation';
import SettingsSectionHeader from './settings/SettingsSectionHeader';
import UserRolesSection from './settings/UserRolesSection';
import PermissionsSection from './settings/PermissionsSection';
import StaffAccessSection from './settings/StaffAccessSection';
import BranchPicker from './settings/BranchPicker';
import { parseRolePermissions } from '../utils/rolePermissions';
import { canEditRolePermissions } from '../utils/navPermissions';
import {
  canAccessSettingsSection,
  firstAccessibleSettingsSection,
  resolveSettingsNav,
} from '../utils/settingsPermissions';
import { useToast } from '../hooks/use-toast';
import { syncAcademicYearToLocalStorage } from '../hooks/useAcademicYearBootstrap';
import { useAuth } from '../context/AuthContext';
import { getSchoolFeatures, isSchoolFeatureEnabled } from '../utils/schoolFeatures';
import SlipCategoryToggle from './slips/SlipCategoryToggle';
import SlipFormatSelector from './slips/SlipFormatSelector';
import SlipTemplatePreviewModal from './slips/SlipTemplatePreviewModal';
import CertificateFormatSelector from './reports/settings/CertificateFormatSelector';
import CertificateTemplatePreviewModal from './reports/settings/CertificateTemplatePreviewModal';
import PlatformSettings from './settings/PlatformSettings';
import SchoolSubscription from './subscription/SchoolSubscription';
import { isSuperAdminSettingsUser } from '../utils/settingsPermissions';
import BiometricAttendanceSection from './settings/biometric/BiometricAttendanceSection';

const PRIMARY_BTN = 'bg-blue-600 hover:bg-blue-700 text-white';
const WEEKDAY_DEFS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

function SettingsCard({ children, className = '' }) {
  return (
    <Card className={`rounded-2xl border-border shadow-sm ${className}`}>
      {children}
    </Card>
  );
}

/** API '' = no encryption; null/undefined = default ssl */
function smtpCryptoFromApi(val) {
  if (val === '') return 'none';
  return val || 'ssl';
}

function smtpCryptoToApi(val) {
  return val === 'none' ? '' : (val || 'ssl');
}

const Settings = () => {
  const { user } = useAuth();
  if (isSuperAdminSettingsUser(user)) {
    return <PlatformSettings />;
  }
  return <SchoolSettings />;
};

const SchoolSettings = () => {
  const { user, patchUser, refreshUser } = useAuth();
  const [schools, setSchools] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [roles, setRoles] = useState([]);
  const [schoolUsers, setSchoolUsers] = useState([]);
  const [schoolUsersLoading, setSchoolUsersLoading] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState(null);
  const [permissionSavingKey, setPermissionSavingKey] = useState('');
  const [settings, setSettings] = useState({});
  const [schoolDetails, setSchoolDetails] = useState(null);
  const [schoolDetailsLoading, setSchoolDetailsLoading] = useState(false);
  const [schoolDetailsSaving, setSchoolDetailsSaving] = useState(false);
  const [schoolPhoneError, setSchoolPhoneError] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);
  const signatureInputRef = useRef(null);
  const footerBgInputRef = useRef(null);
  const [signatureUploading, setSignatureUploading] = useState(false);
  const [footerBgUploading, setFooterBgUploading] = useState(false);
  const [footerCropOpen, setFooterCropOpen] = useState(false);
  const [footerCropSrc, setFooterCropSrc] = useState(null);
  const [idCardSaving, setIdCardSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('general.settings');
  const [expandedGroup, setExpandedGroup] = useState('general');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { toast } = useToast();

  const [yearForm, setYearForm] = useState({ name: '', start_date: '', end_date: '', is_current: false });
  const [updatingYearId, setUpdatingYearId] = useState(null);
  const [detectedYear, setDetectedYear] = useState(null);
  const [schoolFeatures, setSchoolFeatures] = useState({
    is_library_enabled: true,
    is_transport_enabled: true,
    is_branches_enabled: false,
    leave_require_class_teacher_approval: true,
    leave_require_admin_approval: true,
    leave_extra_approver_user_ids: [],
  });
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [featuresSaving, setFeaturesSaving] = useState(false);
  const [slipsCategory, setSlipsCategory] = useState('salary_slip');
  const [slipsTemplatesByCat, setSlipsTemplatesByCat] = useState({ salary_slip: null, fee_receipt: null });
  const [slipsLoading, setSlipsLoading] = useState(false);
  const [slipsSavingId, setSlipsSavingId] = useState('');
  const [slipsPreviewOpen, setSlipsPreviewOpen] = useState(false);
  const [slipsPreviewTemplate, setSlipsPreviewTemplate] = useState(null);
  const [certTemplatesData, setCertTemplatesData] = useState(null);
  const [certTemplatesLoading, setCertTemplatesLoading] = useState(false);
  const [certTemplateSaving, setCertTemplateSaving] = useState(false);
  const [certPreviewOpen, setCertPreviewOpen] = useState(false);
  const [certPreviewTemplate, setCertPreviewTemplate] = useState(null);
  const [idCardTemplatesData, setIdCardTemplatesData] = useState(null);
  const [idCardTemplatesLoading, setIdCardTemplatesLoading] = useState(false);
  const [idCardTemplateSaving, setIdCardTemplateSaving] = useState(false);
  const [emailSettings, setEmailSettings] = useState({
    smtp_host: '',
    smtp_port: '465',
    smtp_user: '',
    smtp_pass: '',
    smtp_crypto: 'ssl',
    smtp_from_email: '',
    smtp_from_name: '',
    admission_emails_enabled: true,
  });
  const [emailPortalUrl, setEmailPortalUrl] = useState('');
  const [emailSettingsLoading, setEmailSettingsLoading] = useState(false);
  const [emailSettingsSaving, setEmailSettingsSaving] = useState(false);
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState('');
  const [whatsappSettings, setWhatsappSettings] = useState({
    waba_id: '',
    phone_number_id: '',
    access_token: '',
    webhook_verify_token: '',
    app_secret: '',
    default_language: 'en',
    enabled: false,
    announcements_enabled: true,
    queue_batch_size: '50',
    retry_count: '3',
  });
  const [whatsappSettingsLoading, setWhatsappSettingsLoading] = useState(false);
  const [whatsappSettingsSaving, setWhatsappSettingsSaving] = useState(false);
  const [whatsappConfigured, setWhatsappConfigured] = useState(false);
  const [testWhatsappSending, setTestWhatsappSending] = useState(false);
  const [testWhatsappPhone, setTestWhatsappPhone] = useState('');

  const isSuperAdmin = user?.role_slug === 'super_admin';
  const isSchoolAdmin = user?.role_slug === 'school_admin';
  const canManageStaffRoles = isSuperAdmin || user?.role_slug === 'school_admin';
  const canEditPermissions = canEditRolePermissions(user);
  const settingsNav = useMemo(() => resolveSettingsNav(user), [user]);
  const currentSchoolId = isSuperAdmin
    ? (settings.current_school || user?.school_id || (schools[0]?.id?.toString()))
    : (user?.school_id?.toString() || '');

  const idCardAssets = useIdCardAssets(currentSchoolId);
  const getTilePreviewUrl = idCardAssets.getTilePreviewUrl;

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (!user) return;
    if (canAccessSettingsSection(user, activeSection)) return;
    const fallback = firstAccessibleSettingsSection(user);
    if (fallback) {
      setActiveSection(fallback);
      const group = settingsNav.find((g) => g.items.some((i) => i.id === fallback));
      if (group) setExpandedGroup(group.key);
    }
  }, [user, activeSection, settingsNav]);

  useEffect(() => {
    setTestEmailTo((prev) => prev || user?.email || '');
  }, [user?.email]);

  const getAcademicYearInfo = (date = new Date()) => {
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    const startYear = month >= 7 ? year : year - 1;
    const endYear = startYear + 1;
    return {
      label: `${startYear}-${endYear}`,
      startYear,
      endYear,
      startDate: `${startYear}-07-01`,
      endDate: `${endYear}-06-30`,
    };
  };

  useEffect(() => {
    const info = getAcademicYearInfo();
    setDetectedYear(info);
    setYearForm((prev) => {
      if (prev.name || prev.start_date || prev.end_date) return prev;
      return { name: info.label, start_date: info.startDate, end_date: info.endDate, is_current: true };
    });
  }, [currentSchoolId]);

  useEffect(() => {
    if (currentSchoolId) fetchSchoolDetails(currentSchoolId);
    else setSchoolDetails(null);
  }, [currentSchoolId]);

  const idCardAssetHydrateKey = useMemo(() => {
    if (!schoolDetails) return '';
    return [
      schoolDetails.logo || '',
      schoolDetails.principal_signature || '',
      schoolDetails.id_card_footer_bg || '',
      schoolDetails.updated_at || '',
    ].join('|');
  }, [schoolDetails]);

  useEffect(() => {
    if (schoolDetails && currentSchoolId) {
      idCardAssets.hydrateFromSchool(schoolDetails, { cacheToken: Date.now() });
    }
  }, [idCardAssetHydrateKey, currentSchoolId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (footerCropSrc && String(footerCropSrc).startsWith('blob:')) {
        URL.revokeObjectURL(footerCropSrc);
      }
    };
  }, [footerCropSrc]);

  const fetchSchoolUsers = useCallback(async (options = {}) => {
    if (!currentSchoolId) {
      setSchoolUsers([]);
      return;
    }
    setSchoolUsersLoading(true);
    try {
      const params = { school_id: currentSchoolId };
      if (options.forLeaveApprovers) {
        params.for_leave_approvers = true;
      }
      const res = await api.get('/settings/school-users', { params });
      setSchoolUsers(res.data.data || []);
    } catch {
      setSchoolUsers([]);
    } finally {
      setSchoolUsersLoading(false);
    }
  }, [currentSchoolId]);

  useEffect(() => {
    if (activeSection === 'roles.userRoles' || activeSection === 'roles.permissions' || activeSection === 'roles.staffAccess') {
      fetchSchoolUsers();
    }
  }, [activeSection, fetchSchoolUsers]);

  const fetchSchoolFeatures = useCallback(async () => {
    if (!currentSchoolId) return;
    setFeaturesLoading(true);
    try {
      const res = await api.get(`/schools/${currentSchoolId}/features`);
      const data = res.data.data || {};
      setSchoolFeatures(getSchoolFeatures(data));
    } catch {
      setSchoolFeatures(getSchoolFeatures(user));
    } finally {
      setFeaturesLoading(false);
    }
  }, [currentSchoolId, user]);

  useEffect(() => {
    if (activeSection === 'features.management' && currentSchoolId) {
      fetchSchoolFeatures();
      fetchSchoolUsers({ forLeaveApprovers: true });
    }
  }, [activeSection, currentSchoolId, fetchSchoolFeatures, fetchSchoolUsers]);

  const fetchEmailSettings = useCallback(async () => {
    if (!currentSchoolId && !isSuperAdmin) return;
    setEmailSettingsLoading(true);
    try {
      const params = currentSchoolId ? { school_id: currentSchoolId } : {};
      const res = await api.get('/settings/email', { params });
      const s = res.data?.data?.settings || {};
      setEmailPortalUrl(res.data?.data?.portal_url || '');
      setEmailSettings({
        smtp_host: s.smtp_host || '',
        smtp_port: s.smtp_port || '465',
        smtp_user: s.smtp_user || '',
        smtp_pass: '',
        smtp_crypto: smtpCryptoFromApi(s.smtp_crypto),
        smtp_from_email: s.smtp_from_email || '',
        smtp_from_name: s.smtp_from_name || '',
        admission_emails_enabled: s.admission_emails_enabled !== '0' && s.admission_emails_enabled !== 'false',
      });
    } catch {
      toast({ title: 'Error', description: 'Failed to load email settings', variant: 'destructive' });
    } finally {
      setEmailSettingsLoading(false);
    }
  }, [currentSchoolId, isSuperAdmin, toast]);

  useEffect(() => {
    if (activeSection === 'general.email') {
      fetchEmailSettings();
    }
  }, [activeSection, fetchEmailSettings]);

  const fetchWhatsappSettings = useCallback(async () => {
    if (!currentSchoolId && !isSuperAdmin) return;
    setWhatsappSettingsLoading(true);
    try {
      const res = await api.get('/settings/whatsapp', {
        params: currentSchoolId ? { school_id: currentSchoolId } : {},
      });
      const s = res.data?.data?.settings || {};
      setWhatsappConfigured(!!res.data?.data?.configured);
      setWhatsappSettings({
        waba_id: s.waba_id || '',
        phone_number_id: s.phone_number_id || '',
        access_token: '',
        webhook_verify_token: '',
        app_secret: '',
        default_language: s.default_language || 'en',
        enabled: s.enabled === true || s.enabled === '1' || s.enabled === 1,
        announcements_enabled: s.announcements_enabled !== false && s.announcements_enabled !== '0' && s.announcements_enabled !== 0,
        queue_batch_size: String(s.queue_batch_size || '50'),
        retry_count: String(s.retry_count || '3'),
      });
    } catch {
      toast({ title: 'Error', description: 'Failed to load WhatsApp settings', variant: 'destructive' });
    } finally {
      setWhatsappSettingsLoading(false);
    }
  }, [currentSchoolId, isSuperAdmin, toast]);

  useEffect(() => {
    if (activeSection === 'general.whatsapp') {
      fetchWhatsappSettings();
    }
  }, [activeSection, fetchWhatsappSettings]);

  const handleSaveWhatsappSettings = async () => {
    setWhatsappSettingsSaving(true);
    try {
      const payload = {
        school_id: currentSchoolId ? parseInt(currentSchoolId, 10) : null,
        settings: {
          waba_id: whatsappSettings.waba_id,
          phone_number_id: whatsappSettings.phone_number_id,
          default_language: whatsappSettings.default_language,
          enabled: whatsappSettings.enabled,
          announcements_enabled: whatsappSettings.announcements_enabled,
          queue_batch_size: parseInt(whatsappSettings.queue_batch_size, 10) || 50,
          retry_count: parseInt(whatsappSettings.retry_count, 10) || 3,
        },
      };
      if (whatsappSettings.access_token) payload.settings.access_token = whatsappSettings.access_token;
      if (whatsappSettings.webhook_verify_token) payload.settings.webhook_verify_token = whatsappSettings.webhook_verify_token;
      if (whatsappSettings.app_secret) payload.settings.app_secret = whatsappSettings.app_secret;
      await api.post('/settings/whatsapp', payload);
      toast({ title: 'Saved', description: 'WhatsApp settings updated' });
      await refreshUser();
      fetchWhatsappSettings();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Save failed', variant: 'destructive' });
    } finally {
      setWhatsappSettingsSaving(false);
    }
  };

  const handleTestWhatsapp = async () => {
    if (!testWhatsappPhone.trim()) {
      toast({ title: 'Phone required', variant: 'destructive' });
      return;
    }
    setTestWhatsappSending(true);
    try {
      const res = await api.post('/settings/whatsapp/test', {
        phone: testWhatsappPhone.trim(),
        school_id: currentSchoolId ? parseInt(currentSchoolId, 10) : null,
      });
      toast({ title: 'Test queued', description: res.data?.message || 'Check WhatsApp logs for status' });
    } catch (e) {
      toast({ title: 'Test failed', description: e.response?.data?.message || 'Could not send test', variant: 'destructive' });
    } finally {
      setTestWhatsappSending(false);
    }
  };

  const handleSaveEmailSettings = async () => {
    setEmailSettingsSaving(true);
    try {
      await api.post('/settings/email', {
        school_id: currentSchoolId ? parseInt(currentSchoolId, 10) : null,
        settings: {
          ...emailSettings,
          smtp_crypto: smtpCryptoToApi(emailSettings.smtp_crypto),
          admission_emails_enabled: emailSettings.admission_emails_enabled ? '1' : '0',
          ...(emailSettings.smtp_pass ? { smtp_pass: emailSettings.smtp_pass } : {}),
        },
      });
      toast({ title: 'Saved', description: 'Email settings updated' });
      fetchEmailSettings();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.message || 'Save failed', variant: 'destructive' });
    } finally {
      setEmailSettingsSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmailTo?.trim()) {
      toast({ title: 'Email required', description: 'Enter a test recipient address', variant: 'destructive' });
      return;
    }
    setTestEmailSending(true);
    try {
      const res = await api.post('/auth/test-email', {
        email: testEmailTo.trim(),
        ...(currentSchoolId ? { school_id: parseInt(currentSchoolId, 10) } : {}),
      });
      const ok = res.data?.data?.success;
      toast({
        title: ok ? 'Test sent' : 'Send failed',
        description: res.data?.message || (ok ? 'Check the inbox.' : 'Verify SMTP settings.'),
        variant: ok ? 'default' : 'destructive',
      });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.message || 'Test failed', variant: 'destructive' });
    } finally {
      setTestEmailSending(false);
    }
  };

  const saveSchoolFeatures = async (nextFeatures) => {
    if (!currentSchoolId) return;
    const branchesWasEnabled = schoolFeatures.is_branches_enabled;
    setFeaturesSaving(true);
    try {
      const res = await api.put(`/schools/${currentSchoolId}/features`, nextFeatures);
      const data = res.data.data || nextFeatures;
      const next = getSchoolFeatures(data);
      setSchoolFeatures(next);
      if (String(user?.school_id) === String(currentSchoolId)) {
        patchUser({ school_features: next });
        if (next.is_branches_enabled && !branchesWasEnabled) {
          await refreshUser();
          toast({ title: 'Branch switcher enabled', description: 'You can now switch branches from the navbar.' });
        }
        if (!next.is_branches_enabled && branchesWasEnabled) {
          await refreshUser();
          toast({ title: 'Branch switcher disabled', description: 'Branch switcher hidden from the navbar.' });
        }
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to save features',
        variant: 'destructive',
      });
      fetchSchoolFeatures();
    } finally {
      setFeaturesSaving(false);
    }
  };

  const handleFeatureToggle = (field, checked) => {
    const next = { ...schoolFeatures, [field]: checked };
    if (
      !next.leave_require_class_teacher_approval
      && !next.leave_require_admin_approval
    ) {
      next.leave_require_admin_approval = true;
      toast({
        title: 'At least one approval step required',
        description: 'Enable class teacher approval, admin approval, or both.',
        variant: 'destructive',
      });
    }
    setSchoolFeatures(next);
    saveSchoolFeatures(next);
  };

  const handleExtraLeaveApprovers = (userIds) => {
    const next = { ...schoolFeatures, leave_extra_approver_user_ids: userIds };
    setSchoolFeatures(next);
    saveSchoolFeatures(next);
  };

  const toggleExtraLeaveApprover = (userId) => {
    const id = parseInt(userId, 10);
    if (!id) return;
    const current = schoolFeatures.leave_extra_approver_user_ids || [];
    const nextIds = current.includes(id)
      ? current.filter((x) => x !== id)
      : [...current, id];
    handleExtraLeaveApprovers(nextIds);
  };

  const fetchSlipTemplates = useCallback(async (category) => {
    if (!currentSchoolId) return null;
    const c = category || 'salary_slip';
    setSlipsLoading(true);
    try {
      const res = await api.get('/slips/templates', { params: { category: c, school_id: currentSchoolId } });
      return res.data?.data || null;
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Failed to load slip templates', variant: 'destructive' });
      return null;
    } finally {
      setSlipsLoading(false);
    }
  }, [currentSchoolId]);

  useEffect(() => {
    if (activeSection !== 'slips.templates' || !currentSchoolId) return;
    let cancelled = false;
    (async () => {
      const [salary, fee] = await Promise.all([
        fetchSlipTemplates('salary_slip'),
        fetchSlipTemplates('fee_receipt'),
      ]);
      if (cancelled) return;
      setSlipsTemplatesByCat({ salary_slip: salary, fee_receipt: fee });
    })();
    return () => { cancelled = true; };
  }, [activeSection, currentSchoolId, fetchSlipTemplates]);

  const fetchCertTemplates = useCallback(async () => {
    if (!currentSchoolId) return null;
    setCertTemplatesLoading(true);
    try {
      const res = await api.get('/reports/templates', { params: { category: 'certificate', school_id: currentSchoolId } });
      return res.data?.data || null;
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Failed to load certificate templates', variant: 'destructive' });
      return null;
    } finally {
      setCertTemplatesLoading(false);
    }
  }, [currentSchoolId, toast]);

  useEffect(() => {
    if (activeSection !== 'reports.certificates' || !currentSchoolId) return;
    let cancelled = false;
    (async () => {
      const data = await fetchCertTemplates();
      if (!cancelled) setCertTemplatesData(data);
    })();
    return () => { cancelled = true; };
  }, [activeSection, currentSchoolId, fetchCertTemplates]);

  const handleSelectCertTemplate = async (templateId) => {
    if (!currentSchoolId) return;
    setCertTemplateSaving(true);
    try {
      const res = await api.put('/reports/templates/active', {
        school_id: currentSchoolId,
        category: 'certificate',
        template_id: templateId,
      });
      const nextActive = res.data?.data?.active_template_id || templateId;
      setCertTemplatesData((prev) => (prev ? { ...prev, active_template_id: nextActive } : prev));
      toast({ title: 'Saved', description: 'Certificate template saved.' });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Failed to save template', variant: 'destructive' });
      const fresh = await fetchCertTemplates();
      if (fresh) setCertTemplatesData(fresh);
    } finally {
      setCertTemplateSaving(false);
    }
  };

  const openCertPreview = (tpl) => {
    setCertPreviewTemplate(tpl);
    setCertPreviewOpen(true);
  };

  const fetchIdCardTemplates = useCallback(async () => {
    if (!currentSchoolId) return null;
    setIdCardTemplatesLoading(true);
    try {
      const res = await api.get('/reports/templates', { params: { category: 'id_card', school_id: currentSchoolId } });
      return res.data?.data || null;
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Failed to load ID card formats', variant: 'destructive' });
      return null;
    } finally {
      setIdCardTemplatesLoading(false);
    }
  }, [currentSchoolId, toast]);

  useEffect(() => {
    if (activeSection !== 'general.idcard' || !currentSchoolId) return;
    let cancelled = false;
    (async () => {
      const data = await fetchIdCardTemplates();
      if (!cancelled) setIdCardTemplatesData(data);
    })();
    return () => { cancelled = true; };
  }, [activeSection, currentSchoolId, fetchIdCardTemplates]);

  const handleSelectIdCardTemplate = async (templateId) => {
    if (!currentSchoolId) return;
    setIdCardTemplateSaving(true);
    try {
      const res = await api.put('/reports/templates/active', {
        school_id: currentSchoolId,
        category: 'id_card',
        template_id: templateId,
      });
      const nextActive = res.data?.data?.active_template_id || templateId;
      setIdCardTemplatesData((prev) => (prev ? { ...prev, active_template_id: nextActive } : prev));

      const tpl = (idCardTemplatesData?.templates || []).find((t) => String(t.id) === String(templateId));
      const format = tpl?.layout_key?.includes('landscape') ? 'landscape' : 'portrait';
      try {
        await api.put('/reports/id-card/print-settings', {
          school_id: currentSchoolId,
          settings: {
            cardFormat: format,
            cardWidthIn: format === 'landscape' ? 3 : 2,
            cardHeightIn: format === 'landscape' ? 2 : 3,
          },
        });
      } catch {
        // non-fatal — template save succeeded
      }

      toast({ title: 'Saved', description: 'ID card template saved for all generation.' });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Failed to save template', variant: 'destructive' });
      const fresh = await fetchIdCardTemplates();
      if (fresh) setIdCardTemplatesData(fresh);
    } finally {
      setIdCardTemplateSaving(false);
    }
  };

  const handleSelectSlipTemplate = async (category, templateId) => {
    if (!currentSchoolId) return;
    setSlipsSavingId(`${category}:${templateId}`);
    try {
      const res = await api.put('/slips/templates/active', { school_id: currentSchoolId, category, template_id: templateId });
      const nextActive = res.data?.data?.active_template_id || templateId;
      setSlipsTemplatesByCat((prev) => {
        const cur = prev?.[category];
        if (!cur) return prev;
        return { ...prev, [category]: { ...cur, active_template_id: nextActive } };
      });
      toast({ title: 'Saved', description: 'Template selected successfully.' });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Failed to save template', variant: 'destructive' });
      const fresh = await fetchSlipTemplates(category);
      if (fresh) {
        setSlipsTemplatesByCat((prev) => ({ ...prev, [category]: fresh }));
      }
    } finally {
      setSlipsSavingId('');
    }
  };

  const openSlipPreview = (tpl) => {
    setSlipsPreviewTemplate(tpl);
    setSlipsPreviewOpen(true);
  };

  const fetchData = async () => {
    try {
      const [schoolsRes, yearsRes, rolesRes, settingsRes] = await Promise.all([
        api.get('/schools'),
        api.get('/settings/academic-years'),
        api.get('/settings/roles'),
        api.get('/settings'),
      ]);
      setSchools(schoolsRes.data.data || []);
      setAcademicYears(yearsRes.data.data || []);
      setRoles(rolesRes.data.data || []);
      setSettings(settingsRes.data.data || {});
    } catch {
      console.error('Failed to fetch settings data');
    }
  };

  const fetchSchoolDetails = useCallback(async (schoolId) => {
    if (!schoolId) return null;
    setSchoolDetailsLoading(true);
    try {
      const res = await api.get(`/schools/${schoolId}`);
      const data = res.data.data || null;
      setSchoolDetails(data);
      return data;
    } catch {
      setSchoolDetails(null);
      return null;
    } finally {
      setSchoolDetailsLoading(false);
    }
  }, []);

  const handleSelectSection = (sectionId) => {
    if (!canAccessSettingsSection(user, sectionId)) return;
    setActiveSection(sectionId);
    setMobileNavOpen(false);
    const group = settingsNav.find((g) => g.items.some((i) => i.id === sectionId));
    if (group) setExpandedGroup(group.key);
  };

  const handleToggleGroup = (groupKey) => {
    setExpandedGroup((prev) => (prev === groupKey ? null : groupKey));
  };

  const handleSaveSchoolDetails = async (e) => {
    e?.preventDefault();
    if (!currentSchoolId || !schoolDetails) return;
    const err = validateMobile(schoolDetails.phone);
    if (err) {
      setSchoolPhoneError(err);
      toast({ title: 'Invalid mobile number', description: err, variant: 'destructive' });
      return;
    }
    setSchoolPhoneError('');
    setSchoolDetailsSaving(true);
    try {
      await api.put(`schools/${currentSchoolId}`, {
        name: schoolDetails.name,
        code: schoolDetails.code || '',
        logo: schoolDetails.logo || '',
        address: schoolDetails.address || '',
        phone: schoolDetails.phone || null,
        email: schoolDetails.email,
      });
      toast({ title: 'Success', description: 'School information saved' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.message || 'Failed to save school', variant: 'destructive' });
    } finally {
      setSchoolDetailsSaving(false);
    }
  };

  const uploadErrorMessage = (err) => {
    const msg = err.response?.data?.message;
    if (msg) return msg;
    const status = err.response?.status;
    if (status === 404) return 'Upload API not found on server.';
    if (status === 401 || status === 403) return 'Not allowed to upload.';
    if (!err.response) return 'Cannot reach API.';
    return 'Upload failed';
  };

  const finishAssetUpload = useCallback(
    async (assetKey, stateKey, path, uploadData) => {
      setSchoolDetails((prev) => (prev ? { ...prev, [stateKey]: path, updated_at: new Date().toISOString() } : null));
      const previewOk = idCardAssets.applyServerPath(assetKey, path, uploadData);
      const fresh = await fetchSchoolDetails(currentSchoolId);
      if (fresh) idCardAssets.hydrateFromSchool(fresh, { cacheToken: Date.now() });
      if (!previewOk) {
        toast({
          title: 'Saved on server',
          description: 'Image uploaded but preview URL could not be built. Reload the page or check API base URL.',
          variant: 'destructive',
        });
        return false;
      }
      return true;
    },
    [currentSchoolId, idCardAssets, fetchSchoolDetails, toast]
  );

  const handleLogoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !currentSchoolId || !schoolDetails) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Choose JPG, PNG, GIF or WebP', variant: 'destructive' });
      return;
    }
    idCardAssets.setInstantPreview('schoolLogo', file);
    setLogoUploading(true);
    try {
      const prepared = await prepareFileForUpload(file);
      const formData = new FormData();
      formData.append('logo', prepared);
      const res = await api.post(`schools/${currentSchoolId}/logo`, formData);
      const uploadData = res.data?.data ?? {};
      const path = extractUploadedAssetPath(uploadData, 'logo', currentSchoolId);
      if (path) {
        const previewOk = await finishAssetUpload('schoolLogo', 'logo', path, uploadData);
        if (previewOk) toast({ title: 'Success', description: 'Logo uploaded' });
      } else {
        toast({
          title: 'Upload incomplete',
          description: 'File may be saved but school record was not updated. Run DB migrations or retry.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      toast({ title: 'Error', description: uploadErrorMessage(err), variant: 'destructive' });
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const uploadSchoolAsset = useCallback(
    (assetKey, endpoint, fieldName, stateKey, setUploading, inputRef, successMsg) => async (file) => {
      if (!file || !currentSchoolId || !schoolDetails) return;
      if (!file.type.startsWith('image/')) {
        toast({ title: 'Invalid file', description: 'Choose JPG, PNG, GIF or WebP', variant: 'destructive' });
        return;
      }
      idCardAssets.setInstantPreview(assetKey, file);
      setUploading(true);
      try {
        const prepared = await prepareFileForUpload(file);
        const formData = new FormData();
        formData.append(fieldName, prepared);
        const res = await api.post(`schools/${currentSchoolId}/${endpoint}`, formData);
        const uploadData = res.data?.data ?? {};
        const path = extractUploadedAssetPath(uploadData, stateKey, currentSchoolId);
        if (path) {
          const previewOk = await finishAssetUpload(assetKey, stateKey, path, uploadData);
          if (previewOk) toast({ title: 'Success', description: successMsg });
        } else {
          toast({
            title: 'Upload incomplete',
            description: 'File may be saved but school record was not updated. Run DB migrations or retry.',
            variant: 'destructive',
          });
        }
      } catch (err) {
        toast({ title: 'Error', description: uploadErrorMessage(err), variant: 'destructive' });
      } finally {
        setUploading(false);
        if (inputRef?.current) inputRef.current.value = '';
      }
    },
    [currentSchoolId, schoolDetails, idCardAssets, fetchSchoolDetails, toast, finishAssetUpload]
  );

  const uploadFooterBackground = uploadSchoolAsset(
    'footerBackground',
    'id-card-footer-bg',
    'background',
    'id_card_footer_bg',
    setFooterBgUploading,
    footerBgInputRef,
    'Footer banner saved'
  );

  const closeFooterCrop = useCallback(() => {
    setFooterCropOpen(false);
    if (footerCropSrc && String(footerCropSrc).startsWith('blob:')) {
      URL.revokeObjectURL(footerCropSrc);
    }
    setFooterCropSrc(null);
  }, [footerCropSrc]);

  const handleFooterBgPick = (e) => {
    const file = e.target.files?.[0];
    if (!file || !currentSchoolId || !schoolDetails) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Choose JPG, PNG, GIF or WebP', variant: 'destructive' });
      return;
    }
    if (footerCropSrc && String(footerCropSrc).startsWith('blob:')) {
      URL.revokeObjectURL(footerCropSrc);
    }
    setFooterCropSrc(URL.createObjectURL(file));
    setFooterCropOpen(true);
    if (footerBgInputRef.current) footerBgInputRef.current.value = '';
  };

  const handleFooterCropConfirm = async (croppedFile) => {
    setFooterCropOpen(false);
    await uploadFooterBackground(croppedFile);
    closeFooterCrop();
  };

  const handleSaveIdCardSetup = async () => {
    if (!currentSchoolId || !schoolDetails) return;
    setIdCardSaving(true);
    try {
      await api.put(`schools/${currentSchoolId}`, {
        website: schoolDetails.website || '',
        id_card_managed_org_name: schoolDetails.id_card_managed_org_name || '',
      });
      toast({ title: 'Success', description: 'ID card settings saved' });
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.message || 'Failed to save', variant: 'destructive' });
    } finally {
      setIdCardSaving(false);
    }
  };

  const handleSignaturePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !currentSchoolId || !schoolDetails) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Choose JPG, PNG, GIF or WebP', variant: 'destructive' });
      return;
    }
    idCardAssets.setInstantPreview('principalSignature', file);
    setSignatureUploading(true);
    try {
      const prepared = await prepareFileForUpload(file);
      const formData = new FormData();
      formData.append('signature', prepared);
      const res = await api.post(`schools/${currentSchoolId}/principal-signature`, formData);
      const uploadData = res.data?.data ?? {};
      const path = extractUploadedAssetPath(uploadData, 'principal_signature', currentSchoolId);
      if (path) {
        setSchoolDetails((prev) => {
          if (!prev) return null;
          const next = { ...prev, principal_signature: path, updated_at: new Date().toISOString() };
          if (String(prev.logo || '').includes('principal_signature')) next.logo = null;
          return next;
        });
        const previewOk = idCardAssets.applyServerPath('principalSignature', path, uploadData);
        const fresh = await fetchSchoolDetails(currentSchoolId);
        if (fresh) idCardAssets.hydrateFromSchool(fresh, { cacheToken: Date.now() });
        if (previewOk) {
          toast({ title: 'Success', description: 'Principal signature saved' });
        } else {
          toast({
            title: 'Saved on server',
            description: 'Signature uploaded but preview URL could not be built. Reload the page.',
            variant: 'destructive',
          });
        }
      } else {
        toast({
          title: 'Upload incomplete',
          description: 'File may be saved but school record was not updated. Run DB migrations or retry.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      toast({ title: 'Error', description: uploadErrorMessage(err), variant: 'destructive' });
    } finally {
      setSignatureUploading(false);
      if (signatureInputRef.current) signatureInputRef.current.value = '';
    }
  };

  const handleSaveSettings = async () => {
    try {
      await api.post('/settings', { settings });
      if (settings.current_academic_year && currentSchoolId) {
        syncAcademicYearToLocalStorage(currentSchoolId, settings.current_academic_year);
      }
      toast({ title: 'Success', description: 'Settings saved' });
    } catch {
      toast({ title: 'Error', description: 'Failed to save', variant: 'destructive' });
    }
  };

  const parseWeekendDays = useCallback((raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.map(String);
    if (typeof raw !== 'string') return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map(String);
    } catch { /* ignore */ }
    return [];
  }, []);

  const weekendDays = useMemo(() => {
    const days = parseWeekendDays(settings.attendance_weekend_days);
    const allowed = new Set(WEEKDAY_DEFS.map((d) => d.key));
    return days.filter((d) => allowed.has(d));
  }, [settings.attendance_weekend_days, parseWeekendDays]);

  const toggleWeekendDay = (dayKey, enabled) => {
    const next = new Set(weekendDays);
    if (enabled) next.add(dayKey);
    else next.delete(dayKey);
    const ordered = WEEKDAY_DEFS.map((d) => d.key).filter((k) => next.has(k));
    setSettings((prev) => ({ ...prev, attendance_weekend_days: JSON.stringify(ordered) }));
  };

  const handleCreateYear = async (e) => {
    e.preventDefault();
    try {
      const schoolId = currentSchoolId;
      const existing = academicYears.find((y) => y.name === yearForm.name && (!schoolId || String(y.school_id) === String(schoolId)));
      if (existing) {
        toast({ title: 'Already exists', description: 'This academic year already exists.', variant: 'destructive' });
        return;
      }
      const hasCurrent = academicYears.some((y) => y.is_current === true || y.is_current === 1 || String(y.is_current) === '1');
      await api.post('/settings/academic-years', { ...yearForm, is_current: !hasCurrent, school_id: schoolId });
      toast({ title: 'Success', description: 'Academic year created' });
      setYearForm({ name: '', start_date: '', end_date: '', is_current: false });
      fetchData();
    } catch {
      toast({ title: 'Error', description: 'Failed', variant: 'destructive' });
    }
  };

  const handleSetCurrentYear = async (yearId) => {
    if (!yearId || updatingYearId) return;
    setUpdatingYearId(yearId);
    try {
      await api.put(`/settings/academic-years/${yearId}/current`, { school_id: currentSchoolId });
      setAcademicYears((prev) => prev.map((y) => ({ ...y, is_current: String(y.id) === String(yearId) ? 1 : 0 })));
      setSettings((prev) => ({ ...prev, current_academic_year: String(yearId) }));
      if (currentSchoolId) {
        syncAcademicYearToLocalStorage(currentSchoolId, yearId);
      }
      toast({ title: 'Success', description: 'Academic year updated' });
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.message || 'Failed to update academic year', variant: 'destructive' });
    } finally {
      setUpdatingYearId(null);
    }
  };

  const handleUserRoleChange = async (userId, roleId) => {
    if (!canManageStaffRoles || !currentSchoolId) return;
    setUpdatingUserId(userId);
    try {
      const res = await api.put(`/settings/school-users/${userId}/role`, {
        role_id: Number(roleId),
        school_id: currentSchoolId,
      });
      const updated = res.data?.data;
      if (updated) {
        setSchoolUsers((prev) => prev.map((u) => (String(u.id) === String(userId) ? updated : u)));
      } else {
        fetchSchoolUsers();
      }
      toast({ title: 'Success', description: 'User role updated' });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to update role',
        variant: 'destructive',
      });
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handlePermissionToggle = async (role, permissionKey, enabled) => {
    if (!canEditPermissions) return;
    const current = parseRolePermissions(role);
    const next = enabled
      ? [...new Set([...current, permissionKey])]
      : current.filter((p) => p !== permissionKey);
    const cellKey = `${role.id}:${permissionKey}`;
    setPermissionSavingKey(cellKey);
    try {
      const res = await api.put(`/settings/roles/${role.id}/permissions`, { permissions: next });
      const updated = res.data?.data;
      if (updated) {
        setRoles((prev) => prev.map((r) => (String(r.id) === String(role.id) ? updated : r)));
      }
      const savedPerms = parseRolePermissions(updated);
      const grantFailed = enabled && !savedPerms.includes(permissionKey);
      if (grantFailed) {
        toast({
          title: 'Not saved',
          description: 'Permission was not saved. Please retry or contact support.',
          variant: 'destructive',
        });
      } else {
        const roleSlug = updated?.slug ?? role.slug;
        if (roleSlug && user?.role_slug && String(roleSlug) === String(user.role_slug)) {
          await refreshUser();
        }
        toast({
          title: 'Saved',
          description:
            roleSlug && user?.role_slug && String(roleSlug) === String(user.role_slug)
              ? `${role.name} permissions updated and applied to your session.`
              : `${role.name} permissions updated. Users with this role must reload the app to apply.`,
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to update permissions',
        variant: 'destructive',
      });
    } finally {
      setPermissionSavingKey('');
    }
  };

  const renderGeneralSettings = () => (
    <>
      <SettingsSectionHeader
        sectionId="general.settings"
        description="System-wide defaults for school context, dates, and currency."
      />
      <SettingsCard>
        <CardContent className="p-6 space-y-4">
          {isSuperAdmin ? (
            <div>
              <Label>School</Label>
              <Select value={settings.current_school || ''} onValueChange={(v) => setSettings({ ...settings, current_school: v })}>
                <SelectTrigger className="mt-1.5 rounded-lg"><SelectValue placeholder="Select School" /></SelectTrigger>
                <SelectContent>
                  {schools.map((s) => <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div>
              <Label>School</Label>
              <div className="mt-1.5 px-3 py-2 rounded-lg border border-border bg-muted/30 text-sm">{schoolDetails?.name || 'Loading…'}</div>
            </div>
          )}
          {isSchoolAdmin && isSchoolFeatureEnabled(user, 'branches') ? <BranchPicker /> : null}
          <div>
            <Label>Academic Year</Label>
            <Select value={settings.current_academic_year || ''} onValueChange={(v) => setSettings({ ...settings, current_academic_year: v })}>
              <SelectTrigger className="mt-1.5 rounded-lg"><SelectValue placeholder="Select Year" /></SelectTrigger>
              <SelectContent>
                {academicYears.map((y) => <SelectItem key={y.id} value={y.id.toString()}>{y.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Date Format</Label>
              <Select value={settings.date_format || 'DD/MM/YYYY'} onValueChange={(v) => setSettings({ ...settings, date_format: v })}>
                <SelectTrigger className="mt-1.5 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                  <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                  <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Currency</Label>
              <Select value={settings.currency || 'INR'} onValueChange={(v) => setSettings({ ...settings, currency: v })}>
                <SelectTrigger className="mt-1.5 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="INR">INR (₹)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={handleSaveSettings} className={PRIMARY_BTN}>
            <Save className="w-4 h-4 mr-2" /> Save Settings
          </Button>
        </CardContent>
      </SettingsCard>
    </>
  );

  const renderAttendanceSettings = () => (
    <>
      <SettingsSectionHeader
        sectionId="academic.attendance"
        description="Configure off-days used to calculate working days in attendance reports."
      />
      <SettingsCard>
        <CardContent className="p-6 space-y-4">
          <div className="rounded-xl border border-border bg-muted/20 p-4">
            <p className="font-medium text-foreground">Weekend / off-days</p>
            <p className="text-xs text-muted-foreground mt-1">
              Selected days excluded from working days. Holidays also excluded.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
              {WEEKDAY_DEFS.map((d) => {
                const checked = weekendDays.includes(d.key);
                return (
                  <label
                    key={d.key}
                    className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) => toggleWeekendDay(d.key, Boolean(v))}
                    />
                    <span className="font-medium text-foreground">{d.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <Button onClick={handleSaveSettings} className={PRIMARY_BTN}>
            <Save className="w-4 h-4 mr-2" /> Save Settings
          </Button>
        </CardContent>
      </SettingsCard>
    </>
  );

  const renderSchoolInformation = () => (
    <>
      <SettingsSectionHeader
        sectionId="general.school"
        description="Name, logo, and contact details used on receipts, reports, and ID cards."
      />
      {!currentSchoolId ? (
        <SettingsCard><CardContent className="p-6 text-sm text-muted-foreground">{isSuperAdmin ? 'Select a school in General Settings first.' : 'No school assigned.'}</CardContent></SettingsCard>
      ) : schoolDetailsLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
      ) : schoolDetails ? (
        <SettingsCard>
          <CardContent className="p-6">
            <form onSubmit={handleSaveSchoolDetails} className="space-y-4">
              <div>
                <Label className="flex items-center gap-2"><School className="w-4 h-4" /> School Name</Label>
                <Input value={schoolDetails.name || ''} onChange={(e) => setSchoolDetails({ ...schoolDetails, name: e.target.value })} className="mt-1.5 rounded-lg" placeholder="ABC Public School" />
              </div>
              <div>
                <Label>School short name</Label>
                <Input value={schoolDetails.code || ''} onChange={(e) => setSchoolDetails({ ...schoolDetails, code: e.target.value.toUpperCase() })} className="mt-1.5 rounded-lg font-mono" placeholder="SMD" />
                <p className="text-xs text-muted-foreground mt-1">Used for teacher employee IDs (SMD-001).</p>
              </div>
              <div>
                <Label className="flex items-center gap-2"><Image className="w-4 h-4" /> School Logo</Label>
                <div className="flex flex-wrap items-center gap-3 mt-2">
                  <input ref={logoInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleLogoPick} className="hidden" />
                  <Button type="button" variant="outline" onClick={() => logoInputRef.current?.click()} disabled={logoUploading} className="rounded-lg">
                    {logoUploading ? 'Uploading…' : 'Choose image'}
                  </Button>
                  {getTilePreviewUrl('schoolLogo') && (
                    <img src={getTilePreviewUrl('schoolLogo')} key={`school-logo-${idCardAssets.version}`} alt="School logo" className="h-12 w-12 object-contain rounded-lg border border-border bg-muted/30" />
                  )}
                </div>
              </div>
              <div>
                <Label className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Address</Label>
                <Input value={schoolDetails.address || ''} onChange={(e) => setSchoolDetails({ ...schoolDetails, address: e.target.value })} className="mt-1.5 rounded-lg" placeholder="Street, City, State" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Phone</Label><MobileInput value={schoolDetails.phone || ''} onChange={(v) => { setSchoolDetails({ ...schoolDetails, phone: v }); setSchoolPhoneError(''); }} error={schoolPhoneError} className="mt-1.5 rounded-lg" /></div>
                <div><Label>Email</Label><Input type="email" value={schoolDetails.email || ''} onChange={(e) => setSchoolDetails({ ...schoolDetails, email: e.target.value })} className="mt-1.5 rounded-lg" /></div>
              </div>
              <Button type="submit" className={PRIMARY_BTN} disabled={schoolDetailsSaving}>
                <Save className="w-4 h-4 mr-2" /> {schoolDetailsSaving ? 'Saving…' : 'Save School Information'}
              </Button>
            </form>
          </CardContent>
        </SettingsCard>
      ) : (
        <SettingsCard><CardContent className="p-6 text-sm text-muted-foreground">Could not load school details.</CardContent></SettingsCard>
      )}
    </>
  );

  const renderEmailSmtp = () => (
    <>
      <SettingsSectionHeader
        sectionId="general.email"
        description="SMTP for admission credential emails and system notifications. Set app.frontendUrl in server .env for login links."
      />
      {!currentSchoolId ? (
        <SettingsCard><CardContent className="p-6 text-sm text-muted-foreground">{isSuperAdmin ? 'Select a school in General Settings first.' : 'No school assigned.'}</CardContent></SettingsCard>
      ) : emailSettingsLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
      ) : (
        <SettingsCard>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between rounded-lg border border-border p-4">
              <div>
                <p className="font-medium text-foreground">Admission credential emails</p>
                <p className="text-xs text-muted-foreground mt-1">Send student + guardian emails when a new admission is created.</p>
              </div>
              <Switch
                checked={!!emailSettings.admission_emails_enabled}
                onCheckedChange={(v) => setEmailSettings({ ...emailSettings, admission_emails_enabled: v })}
              />
            </div>
            {emailPortalUrl ? (
              <p className="text-xs text-muted-foreground">ERP portal URL: <span className="font-mono">{emailPortalUrl}/login</span></p>
            ) : (
              <p className="text-xs text-amber-600 dark:text-amber-400">Set app.frontendUrl in backend .env so emails include the login link.</p>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>SMTP host</Label><Input value={emailSettings.smtp_host} onChange={(e) => setEmailSettings({ ...emailSettings, smtp_host: e.target.value })} className="mt-1.5 rounded-lg" placeholder="smtp.example.com" /></div>
              <div><Label>SMTP port</Label><Input value={emailSettings.smtp_port} onChange={(e) => setEmailSettings({ ...emailSettings, smtp_port: e.target.value })} className="mt-1.5 rounded-lg" placeholder="465" /></div>
              <div><Label>SMTP user</Label><Input value={emailSettings.smtp_user} onChange={(e) => setEmailSettings({ ...emailSettings, smtp_user: e.target.value })} className="mt-1.5 rounded-lg" /></div>
              <div><Label>SMTP password</Label><Input type="password" value={emailSettings.smtp_pass} onChange={(e) => setEmailSettings({ ...emailSettings, smtp_pass: e.target.value })} className="mt-1.5 rounded-lg" placeholder="Leave blank to keep existing" /></div>
              <div>
                <Label>Encryption</Label>
                <Select value={emailSettings.smtp_crypto || 'ssl'} onValueChange={(v) => setEmailSettings({ ...emailSettings, smtp_crypto: v })}>
                  <SelectTrigger className="mt-1.5 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ssl">SSL</SelectItem>
                    <SelectItem value="tls">TLS</SelectItem>
                    <SelectItem value="none">None</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>From email</Label><Input type="email" value={emailSettings.smtp_from_email} onChange={(e) => setEmailSettings({ ...emailSettings, smtp_from_email: e.target.value })} className="mt-1.5 rounded-lg" /></div>
              <div className="sm:col-span-2"><Label>From name</Label><Input value={emailSettings.smtp_from_name} onChange={(e) => setEmailSettings({ ...emailSettings, smtp_from_name: e.target.value })} className="mt-1.5 rounded-lg" /></div>
            </div>
            <div className="border-t border-border pt-4 flex flex-col sm:flex-row gap-3 sm:items-end">
              <div className="flex-1">
                <Label className="flex items-center gap-2"><Mail className="w-4 h-4" /> Test recipient</Label>
                <Input type="email" value={testEmailTo} onChange={(e) => setTestEmailTo(e.target.value)} className="mt-1.5 rounded-lg" placeholder="you@school.com" />
              </div>
              <Button type="button" variant="outline" onClick={handleTestEmail} disabled={testEmailSending} className="rounded-lg">
                {testEmailSending ? 'Sending…' : 'Send test email'}
              </Button>
            </div>
            <Button onClick={handleSaveEmailSettings} disabled={emailSettingsSaving} className={PRIMARY_BTN}>
              <Save className="w-4 h-4 mr-2" /> {emailSettingsSaving ? 'Saving…' : 'Save email settings'}
            </Button>
          </CardContent>
        </SettingsCard>
      )}
    </>
  );

  const renderWhatsappSettings = () => (
    <>
      <SettingsSectionHeader
        sectionId="general.whatsapp"
        description="Meta WhatsApp Business Cloud API. Tokens are encrypted at rest. Webhook URL: {your-api-base}/api/whatsapp/webhook"
      />
      {!currentSchoolId ? (
        <SettingsCard><CardContent className="p-6 text-sm text-muted-foreground">{isSuperAdmin ? 'Select a school in General Settings first.' : 'No school assigned.'}</CardContent></SettingsCard>
      ) : whatsappSettingsLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
      ) : (
        <SettingsCard>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between rounded-lg border border-border p-4">
              <div>
                <p className="font-medium text-foreground">Enable WhatsApp notifications</p>
                <p className="text-xs text-muted-foreground mt-1">{whatsappConfigured ? 'Configured for this school' : 'Not configured — save credentials below'}</p>
              </div>
              <Switch checked={!!whatsappSettings.enabled} onCheckedChange={(v) => setWhatsappSettings({ ...whatsappSettings, enabled: v })} />
            </div>
            <div className={`flex items-center justify-between rounded-lg border border-border p-4 ${!whatsappSettings.enabled ? 'opacity-60' : ''}`}>
              <div>
                <p className="font-medium text-foreground">Announcements via WhatsApp</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {!whatsappSettings.enabled
                    ? 'Announcements appear under Academics and use in-app notifications. Enable WhatsApp above to also deliver via WhatsApp.'
                    : whatsappSettings.announcements_enabled
                      ? 'Show Announcements under Communication and queue WhatsApp messages when announcements are published.'
                      : 'Show Announcements under Academics and deliver via in-app notifications only (no WhatsApp queue).'}
                </p>
              </div>
              <Switch
                checked={!!whatsappSettings.announcements_enabled}
                disabled={!whatsappSettings.enabled}
                onCheckedChange={(v) => setWhatsappSettings({ ...whatsappSettings, announcements_enabled: v })}
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>WhatsApp Business Account ID</Label><Input value={whatsappSettings.waba_id} onChange={(e) => setWhatsappSettings({ ...whatsappSettings, waba_id: e.target.value })} className="mt-1.5 rounded-lg" /></div>
              <div><Label>Phone Number ID</Label><Input value={whatsappSettings.phone_number_id} onChange={(e) => setWhatsappSettings({ ...whatsappSettings, phone_number_id: e.target.value })} className="mt-1.5 rounded-lg" /></div>
              <div><Label>Permanent Access Token</Label><Input type="password" value={whatsappSettings.access_token} onChange={(e) => setWhatsappSettings({ ...whatsappSettings, access_token: e.target.value })} className="mt-1.5 rounded-lg" placeholder="Leave blank to keep existing" /></div>
              <div><Label>Webhook Verify Token</Label><Input type="password" value={whatsappSettings.webhook_verify_token} onChange={(e) => setWhatsappSettings({ ...whatsappSettings, webhook_verify_token: e.target.value })} className="mt-1.5 rounded-lg" placeholder="Leave blank to keep existing" /></div>
              <div><Label>App Secret (webhook signature)</Label><Input type="password" value={whatsappSettings.app_secret} onChange={(e) => setWhatsappSettings({ ...whatsappSettings, app_secret: e.target.value })} className="mt-1.5 rounded-lg" placeholder="Leave blank to keep existing" /></div>
              <div><Label>Default language</Label><Input value={whatsappSettings.default_language} onChange={(e) => setWhatsappSettings({ ...whatsappSettings, default_language: e.target.value })} className="mt-1.5 rounded-lg" placeholder="en" /></div>
              <div><Label>Queue batch size</Label><Input type="number" min={1} value={whatsappSettings.queue_batch_size} onChange={(e) => setWhatsappSettings({ ...whatsappSettings, queue_batch_size: e.target.value })} className="mt-1.5 rounded-lg" /></div>
              <div><Label>Retry count</Label><Input type="number" min={1} value={whatsappSettings.retry_count} onChange={(e) => setWhatsappSettings({ ...whatsappSettings, retry_count: e.target.value })} className="mt-1.5 rounded-lg" /></div>
            </div>
            <div className="border-t border-border pt-4 flex flex-col sm:flex-row gap-3 sm:items-end">
              <div className="flex-1">
                <Label className="flex items-center gap-2"><MessageCircle className="w-4 h-4" /> Test phone</Label>
                <MobileInput value={testWhatsappPhone} onChange={setTestWhatsappPhone} className="mt-1.5" />
              </div>
              <Button type="button" variant="outline" onClick={handleTestWhatsapp} disabled={testWhatsappSending} className="rounded-lg">
                {testWhatsappSending ? 'Queuing…' : 'Send test message'}
              </Button>
            </div>
            <Button onClick={handleSaveWhatsappSettings} disabled={whatsappSettingsSaving} className={PRIMARY_BTN}>
              <Save className="w-4 h-4 mr-2" /> {whatsappSettingsSaving ? 'Saving…' : 'Save WhatsApp settings'}
            </Button>
          </CardContent>
        </SettingsCard>
      )}
    </>
  );

  const renderIdCardSetup = () => (
    <>
      <SettingsSectionHeader
        sectionId="general.idcard"
        description="Configure signature, footer image, and card text with live print preview."
      />
      {!currentSchoolId ? (
        <SettingsCard><CardContent className="p-6 text-sm text-muted-foreground">{isSuperAdmin ? 'Select a school first.' : 'No school assigned.'}</CardContent></SettingsCard>
      ) : (
        <IdCardSetupPanel
          schoolDetails={schoolDetails}
          setSchoolDetails={setSchoolDetails}
          currentSchoolId={currentSchoolId}
          getTilePreviewUrl={idCardAssets.getTilePreviewUrl}
          cardAssetOverrides={idCardAssets.cardAssetOverrides}
          previewVersion={idCardAssets.version}
          uploadHandlers={{ signature: handleSignaturePick, footer: handleFooterBgPick }}
          uploading={{ signature: signatureUploading, footer: footerBgUploading }}
          onSave={handleSaveIdCardSetup}
          saving={idCardSaving}
          loading={schoolDetailsLoading}
          idCardTemplates={idCardTemplatesData?.templates || []}
          activeIdCardTemplateId={idCardTemplatesData?.active_template_id || ''}
          idCardTemplateSaving={idCardTemplateSaving}
          idCardTemplatesLoading={idCardTemplatesLoading}
          onSaveIdCardTemplate={handleSelectIdCardTemplate}
        />
      )}
    </>
  );

  const renderAcademicYear = () => (
    <>
      <SettingsSectionHeader sectionId="academic.year" description="Create academic years and set the active year for your school." />
      <SettingsCard>
        <CardContent className="p-6">
          <form onSubmit={handleCreateYear} className="space-y-4 mb-6 p-4 rounded-xl bg-muted/30 border border-border">
            <p className="text-xs text-muted-foreground">Add one academic year at a time.</p>
            <div className="grid sm:grid-cols-3 gap-4">
              <div><Label>Name</Label><Input value={yearForm.name} onChange={(e) => setYearForm({ ...yearForm, name: e.target.value })} placeholder="2024-2025" className="mt-1.5 rounded-lg" required /></div>
              <div><Label>Start Date</Label><DateInput value={yearForm.start_date} onChange={(v) => setYearForm({ ...yearForm, start_date: v })} className="mt-1.5 rounded-lg" required /></div>
              <div><Label>End Date</Label><DateInput value={yearForm.end_date} onChange={(v) => setYearForm({ ...yearForm, end_date: v })} className="mt-1.5 rounded-lg" required /></div>
            </div>
            <Button type="submit" className={PRIMARY_BTN}>Add Year</Button>
          </form>
          <div className="space-y-2">
            {academicYears.map((year) => {
              const isCurrent = year.is_current === true || year.is_current === 1 || String(year.is_current) === '1';
              const status = detectedYear && year.name === detectedYear.label ? 'Current' : null;
              return (
                <div key={year.id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-border bg-muted/20">
                  <div>
                    <p className="font-medium text-foreground">{year.name}</p>
                    <p className="text-sm text-muted-foreground">{year.start_date} – {year.end_date}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {isCurrent && <span className="px-2 py-1 bg-green-500/15 text-green-600 dark:text-green-400 rounded-full text-xs font-medium">Active</span>}
                    {!isCurrent && status && <span className="px-2 py-1 bg-muted text-muted-foreground rounded-full text-xs">{status}</span>}
                    <Switch checked={isCurrent} onCheckedChange={(checked) => checked && handleSetCurrentYear(year.id)} disabled={updatingYearId && String(updatingYearId) !== String(year.id)} className="data-[state=checked]:bg-blue-600" />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </SettingsCard>
    </>
  );

  const renderFeatureManagement = () => (
    <SettingsCard>
      <CardContent className="p-6 space-y-6">
        <SettingsSectionHeader
          sectionId="features.management"
          description="Turn Library and Transport on or off for this school. Disabled modules are hidden in the app and their fee types are excluded from fee lists and totals. Fee management is always available."
        />
        {featuresLoading ? (
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </p>
        ) : (
          <div className="space-y-4 max-w-lg">
            {featuresSaving && (
              <p className="text-xs text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" /> Saving…
              </p>
            )}
            <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-4">
              <div>
                <p className="font-medium text-foreground">Library module</p>
                <p className="text-xs text-muted-foreground">Books, members, issue/return</p>
              </div>
              <Switch
                checked={schoolFeatures.is_library_enabled}
                disabled={featuresSaving}
                onCheckedChange={(v) => handleFeatureToggle('is_library_enabled', v)}
              />
            </div>
            <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-4">
              <div>
                <p className="font-medium text-foreground">Transport module</p>
                <p className="text-xs text-muted-foreground">Routes, vehicles, student allocation</p>
              </div>
              <Switch
                checked={schoolFeatures.is_transport_enabled}
                disabled={featuresSaving}
                onCheckedChange={(v) => handleFeatureToggle('is_transport_enabled', v)}
              />
            </div>
            {isSchoolAdmin ? (
              <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-4">
                <div>
                  <p className="font-medium text-foreground">We have other branches</p>
                  <p className="text-xs text-muted-foreground">
                    Enable if your school has multiple campuses. Shows branch switcher in the navbar.
                  </p>
                </div>
                <Switch
                  checked={schoolFeatures.is_branches_enabled}
                  disabled={featuresSaving}
                  onCheckedChange={(v) => handleFeatureToggle('is_branches_enabled', v)}
                />
              </div>
            ) : null}
            <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-4">
              <div>
                <p className="font-medium text-foreground">Class teacher approval for leave</p>
                <p className="text-xs text-muted-foreground">
                  When on, new leave applications go to the class teacher first. Pair with admin approval below.
                </p>
              </div>
              <Switch
                checked={!!schoolFeatures.leave_require_class_teacher_approval}
                disabled={featuresSaving}
                onCheckedChange={(v) => handleFeatureToggle('leave_require_class_teacher_approval', v)}
              />
            </div>
            <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-4">
              <div>
                <p className="font-medium text-foreground">Admin approval needed</p>
                <p className="text-xs text-muted-foreground">
                  When on: class teacher recommends, then principal or admin final-approves. When off: class teacher approval completes the leave.
                </p>
              </div>
              <Switch
                checked={!!schoolFeatures.leave_require_admin_approval}
                disabled={featuresSaving}
                onCheckedChange={(v) => handleFeatureToggle('leave_require_admin_approval', v)}
              />
            </div>
            <div className="rounded-lg border border-border p-4 space-y-3">
              <div>
                <p className="font-medium text-foreground">Extra leave approvers</p>
                <p className="text-xs text-muted-foreground">
                  Optional staff who may final-approve leave (in addition to principal and school admin).
                </p>
              </div>
              {schoolUsersLoading ? (
                <p className="text-xs text-muted-foreground">Loading teachers…</p>
              ) : (
                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                  {(schoolUsers || []).map((u) => {
                      const uid = parseInt(u.id, 10);
                      const selected = (schoolFeatures.leave_extra_approver_user_ids || []).includes(uid);
                      const label = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
                      return (
                        <button
                          key={u.id}
                          type="button"
                          disabled={featuresSaving}
                          onClick={() => toggleExtraLeaveApprover(uid)}
                          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                            selected
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-muted/50 text-foreground border-border hover:border-primary/50'
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  {!schoolUsersLoading && (schoolUsers || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground">No active teachers found for this school.</p>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </SettingsCard>
  );

  const renderSlips = () => {
    const data = slipsTemplatesByCat?.[slipsCategory];
    const templates = data?.templates || [];
    const activeId = data?.active_template_id;

    return (
      <SettingsCard>
        <CardContent className="p-6 space-y-6">
          <SettingsSectionHeader
            sectionId="slips.templates"
            description="Select Format 1–5 for each slip type. Preview below, then Save. All future fee receipts and salary slips use the saved format."
          />

          <div className="flex flex-wrap items-center justify-between gap-3">
            <SlipCategoryToggle value={slipsCategory} onChange={setSlipsCategory} />
            <div className="text-xs text-muted-foreground">
              Saved in database. Used for all future PDFs.
            </div>
          </div>

          {slipsLoading && !data ? (
            <p className="text-sm text-muted-foreground">Loading templates…</p>
          ) : templates.length === 0 ? (
            <div className="text-sm text-muted-foreground">No templates available.</div>
          ) : (
            <SlipFormatSelector
              category={slipsCategory}
              templates={templates}
              activeTemplateId={activeId}
              schoolId={currentSchoolId}
              saving={Boolean(slipsSavingId)}
              onSave={handleSelectSlipTemplate}
              onOpenModal={openSlipPreview}
            />
          )}

          <SlipTemplatePreviewModal
            open={slipsPreviewOpen}
            onOpenChange={setSlipsPreviewOpen}
            category={slipsCategory}
            template={slipsPreviewTemplate}
            schoolId={currentSchoolId}
          />
        </CardContent>
      </SettingsCard>
    );
  };

  const renderCertificates = () => {
    const templates = certTemplatesData?.templates || [];
    const activeId = certTemplatesData?.active_template_id;

    return (
      <SettingsCard>
        <CardContent className="p-6 space-y-6">
          <SettingsSectionHeader
            sectionId="reports.certificates"
            description="Choose the default certificate layout for the Reports → Certificate page. Preview below, then Save."
          />
          {certTemplatesLoading && !certTemplatesData ? (
            <p className="text-sm text-muted-foreground">Loading templates…</p>
          ) : templates.length === 0 ? (
            <div className="text-sm text-muted-foreground">No certificate templates available. Run database migrations.</div>
          ) : (
            <CertificateFormatSelector
              templates={templates}
              activeTemplateId={activeId}
              schoolId={currentSchoolId}
              saving={certTemplateSaving}
              onSave={handleSelectCertTemplate}
              onOpenModal={openCertPreview}
            />
          )}
          <CertificateTemplatePreviewModal
            open={certPreviewOpen}
            onOpenChange={setCertPreviewOpen}
            template={certPreviewTemplate}
            schoolId={currentSchoolId}
          />
        </CardContent>
      </SettingsCard>
    );
  };

  const renderSectionContent = (sectionId) => {
    switch (sectionId) {
      case 'general.settings': return renderGeneralSettings();
      case 'general.school': return renderSchoolInformation();
      case 'general.email': return renderEmailSmtp();
      case 'general.whatsapp': return renderWhatsappSettings();
      case 'general.idcard': return renderIdCardSetup();
      case 'features.management': return renderFeatureManagement();
      case 'slips.templates': return renderSlips();
      case 'reports.certificates': return renderCertificates();
      case 'academic.year': return renderAcademicYear();
      case 'academic.attendance': return renderAttendanceSettings();
      case 'academic.biometric': return <BiometricAttendanceSection />;
      case 'roles.userRoles':
        return <UserRolesSection roles={roles} schoolUsers={schoolUsers} />;
      case 'roles.permissions':
        return (
          <PermissionsSection
            roles={roles}
            canEdit={canEditPermissions}
            savingKey={permissionSavingKey}
            onTogglePermission={handlePermissionToggle}
          />
        );
      case 'roles.staffAccess':
        return (
          <StaffAccessSection
            schoolUsers={schoolUsers}
            roles={roles}
            loading={schoolUsersLoading}
            updatingUserId={updatingUserId}
            onRoleChange={handleUserRoleChange}
            currentUserId={user?.id}
            canManageRoles={canManageStaffRoles}
          />
        );
      case 'billing.subscription':
        return <SchoolSubscription />;
      default: return null;
    }
  };

  const renderContent = () => {
    const sectionId = canAccessSettingsSection(user, activeSection)
      ? activeSection
      : firstAccessibleSettingsSection(user);
    if (!sectionId) {
      return (
        <SettingsCard>
          <CardContent className="p-8 text-center text-muted-foreground">
            No settings sections available for your role.
          </CardContent>
        </SettingsCard>
      );
    }
    return renderSectionContent(sectionId);
  };

  const sidebarProps = {
    navGroups: settingsNav,
    activeSection,
    expandedGroup,
    onSelectSection: handleSelectSection,
    onToggleGroup: handleToggleGroup,
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <SettingsIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" /> Settings
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Configure your school ERP — modern admin control center</p>
        </div>
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="lg:hidden rounded-lg">
              <Menu className="h-4 w-4 mr-2" /> Settings menu
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[min(100vw,20rem)] p-0">
            <SheetHeader className="p-4 border-b"><SheetTitle>Settings</SheetTitle></SheetHeader>
            <SettingsSidebar {...sidebarProps} className="border-0 rounded-none shadow-none" />
          </SheetContent>
        </Sheet>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 items-start">
        <SettingsSidebar {...sidebarProps} className="hidden lg:block" />
        <main className="flex-1 min-w-0 w-full">{renderContent()}</main>
      </div>

      <ImageCropDialog
        open={footerCropOpen}
        onOpenChange={(open) => {
          if (!open) closeFooterCrop();
          else setFooterCropOpen(true);
        }}
        imageSrc={footerCropSrc}
        title="Crop footer banner"
        description="Wide strip above the address bar on the ID card. Drag to reposition and zoom to fit your building or banner image."
        confirming={footerBgUploading}
        outputFileName="id-card-footer.jpg"
        onConfirm={handleFooterCropConfirm}
      />
    </div>
  );
};

export default Settings;
